import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  B2BOrder,
  B2BOrdersFilters,
  B2BPaginationMeta,
} from '../../modules/b2b/orders/types';

export interface B2BOrdersState {
  orders: B2BOrder[];
  activeOrderId: string | null;
  activeOrder: B2BOrder | null;
  filters: B2BOrdersFilters;
  meta: B2BPaginationMeta;
  isLoading: boolean;
  isLoadingActiveOrder: boolean;
  isSavingTourist: boolean;
  deletingTouristId: string | null;
  touristMutationError: string | null;
  error: string | null;
}

const initialState: B2BOrdersState = {
  orders: [],
  activeOrderId: null,
  activeOrder: null,
  filters: {
    query: '',
    status: 'all',
  },
  meta: { page: 1, perPage: 20, total: 0 },
  isLoading: false,
  isLoadingActiveOrder: false,
  isSavingTourist: false,
  deletingTouristId: null,
  touristMutationError: null,
  error: null,
};

export const name = 'b2bOrders';

const b2bOrdersSlice = createSlice({
  name,
  initialState,
  reducers: {
    setOrders(state, { payload }: PayloadAction<B2BOrder[]>) {
      state.orders = payload;
    },
    appendOrders(state, { payload }: PayloadAction<B2BOrder[]>) {
      payload.forEach((order) => {
        if (!state.orders.some(({ id }) => id === order.id)) {
          state.orders.push(order);
        }
      });
    },
    setActiveOrderId(state, { payload }: PayloadAction<string | null>) {
      state.activeOrderId = payload;
    },
    setActiveOrder(state, { payload }: PayloadAction<B2BOrder | null>) {
      state.activeOrder = payload;
    },
    clearActiveOrder(state) {
      state.activeOrderId = null;
      state.activeOrder = null;
      state.isLoadingActiveOrder = false;
      state.touristMutationError = null;
    },
    setFilters(state, { payload }: PayloadAction<Partial<B2BOrdersFilters>>) {
      state.filters = { ...state.filters, ...payload };
    },
    setMeta(state, { payload }: PayloadAction<B2BPaginationMeta>) {
      state.meta = payload;
    },
    setError(state, { payload }: PayloadAction<string | null>) {
      state.error = payload;
    },
    setIsLoading(state, { payload }: PayloadAction<boolean>) {
      state.isLoading = payload;
    },
    setIsLoadingActiveOrder(state, { payload }: PayloadAction<boolean>) {
      state.isLoadingActiveOrder = payload;
    },
    setIsSavingTourist(state, { payload }: PayloadAction<boolean>) {
      state.isSavingTourist = payload;
    },
    setDeletingTouristId(state, { payload }: PayloadAction<string | null>) {
      state.deletingTouristId = payload;
    },
    setTouristMutationError(state, { payload }: PayloadAction<string | null>) {
      state.touristMutationError = payload;
    },
  },
});

export const {
  appendOrders,
  clearActiveOrder,
  setOrders,
  setActiveOrderId,
  setActiveOrder,
  setFilters,
  setMeta,
  setError,
  setDeletingTouristId,
  setIsLoading,
  setIsLoadingActiveOrder,
  setIsSavingTourist,
  setTouristMutationError,
} = b2bOrdersSlice.actions;

export default b2bOrdersSlice.reducer;
